import {
  type Client,
  type ClientResponse,
  type Conversation,
  type ConversationContext,
  type ConversationParticipant,
  type Observability,
  type TriggerParams,
} from '@stripe-internal/conversations-client';

import type {Api} from '../../core/lib/api/api';
import type {AppConfig, AppProfile} from '../../core/types';
import type {
  ControllerError,
  ControllerResult,
  ConversationOverview,
  ConversationsControllerApi,
  CreateConversationParams,
  ListConversationFilters,
} from './types';

type ErrorInput = Omit<ControllerError, 'type'>;

const success = <T>(data: T) => ({type: 'success' as const, data});

const error = ({
  code,
  error: errorMessage,
  errorId,
}: ErrorInput): ControllerError => ({
  type: 'error',
  error: errorMessage,
  ...(code === undefined ? {} : {code}),
  ...(errorId === undefined ? {} : {errorId}),
});

const fromClientError = (
  response: Pick<ClientResponse, 'code' | 'error'> & {
    sentryEventId?: string;
  },
): ControllerError =>
  error({
    code: response.code,
    error: response.error ?? 'Unknown conversation controller error',
    errorId: response.sentryEventId,
  });

export function buildV1ConversationsControllerApi({
  getConfig,
  getProfile,
  getApi,
  client,
}: {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  getObservability: () => Observability;
  client: Client;
}): ConversationsControllerApi {
  function getTriggerParams(params: CreateConversationParams): TriggerParams {
    const config = getConfig();
    const initialMessage = params.initialMessage?.trim();
    return {
      triggerPoint:
        params.context?.triggerParams?.triggerPoint ??
        config.triggerPoint ??
        'unspecified',
      ...params.context?.triggerParams,
      ...(initialMessage ? {initialQuery: initialMessage} : {}),
    };
  }

  function getContext(
    params: CreateConversationParams = {},
  ): ConversationContext {
    const config = getConfig();
    const triggerParams = getTriggerParams(params);
    const participant = getParticipant();

    return {
      userId: participant.id,
      audience: config.audience,
      entryPoint: config.entryPoint,
      locale: config.locale,
      ...params.context,
      triggerParams,
    };
  }

  function getParticipant(): ConversationParticipant {
    const {visitor} = getProfile();

    const participant: ConversationParticipant = {
      id: visitor.id,
      role: 'user',
      displayName: visitor.displayName || 'User',
    };

    return participant;
  }

  async function loadConversation(
    id: string,
    {
      fetch = true,
      connect = false,
      context,
    }: {
      context?: Partial<ConversationContext>;
      fetch?: boolean;
      connect?: boolean;
    } = {},
  ): ControllerResult<Conversation> {
    const participant = getParticipant();
    const contextWithOverrides = getContext({context});

    const response = await client.startConversation({
      conversationId: id,
      participant,
      context: contextWithOverrides,
      connect,
      fetch,
    });

    if ('error' in response) {
      return fromClientError(response);
    }

    const {conversation} = response.data;
    return success(conversation);
  }

  return {
    async create(
      params: CreateConversationParams = {},
    ): ControllerResult<Conversation> {
      const participant = getParticipant();
      const context = getContext(params);

      const response = await client.startConversation({
        skipInProgress: true,
        participant,
        context,
        data: params.data,
        connect: params.connect,
        fetch: params.fetch,
      });

      if ('error' in response) {
        return fromClientError(response);
      }

      const {conversation} = response.data;
      return success(conversation);
    },
    async end(id: string): ControllerResult<void> {
      const participant = getParticipant();
      const response = await client.leaveConversation({
        conversationId: id,
        participant,
      });
      if ('error' in response) {
        return fromClientError(response);
      }
      return success(undefined);
    },
    async get(
      id: string,
      {context}: {context?: Partial<ConversationContext>},
    ): ControllerResult<Conversation | null> {
      const conversation = await loadConversation(id, {context});
      if (conversation.type === 'error') {
        if (conversation.code === 404) {
          return success(null);
        }
        return conversation;
      }
      return conversation;
    },
    async list(
      filters: ListConversationFilters = {},
    ): ControllerResult<ConversationOverview[]> {
      const config = getConfig();
      const api = getApi();

      const response = await api.listConversations({
        audience: config.audience,
        entryPoint: config.entryPoint,
        status: filters.status,
        limit: filters.limit,
        triggerPoint: filters.triggerPoint,
        conversationIds: filters.conversationIds,
      });

      if (response.type === 'error') {
        return error(response);
      }

      return success(response.conversations);
    },
  };
}
